// VERSION 2.2 - Implementierung Spielmodi
// =============================================================================
//  KOMPONENTE: PlayerScoreEntry (Refaktorisierte Version von SingleLaneScoreEntry)
//  Zweck: Zeigt eine Tabelle zur Ergebniseingabe für die Sätze eines Spielers.
// =============================================================================
import React, { useMemo, useRef, useEffect } from 'react';

// GEÄNDERT: Die Komponente ist nun von der "Bahn"-Logik entkoppelt.
// Sie erhält eine Liste von Sätzen ('sets') und rendert diese dynamisch.
const PlayerScoreEntry = ({ sets = [], onSetChange, onFocusChange, activeInput }) => {
  const inputRefs = useRef({});

  useEffect(() => {
    if (activeInput) {
      // GEÄNDERT: Fokus wird basierend auf setIndex statt laneKey gesetzt.
      const { setIndex, field } = activeInput;
      const refKey = `set-${setIndex}-${field}`;
      const ref = inputRefs.current[refKey];
      if (ref) {
        ref.focus();
        ref.select();
      }
    }
  }, [activeInput]);

  // GEÄNDERT: Die Berechnung der Gesamtwerte iteriert nun über das 'sets'-Array.
  const totals = useMemo(() => {
    let totalVolle = 0, totalAbr = 0, totalFw = 0;
    for (const set of sets) {
      totalVolle += Number(set.volle) || 0;
      totalAbr += Number(set.abr) || 0;
      totalFw += Number(set.fw) || 0;
    }
    return { totalVolle, totalAbr, totalGesamt: totalVolle + totalAbr, totalFw };
  }, [sets]);

  const handleFocus = (setIndex, field) => {
    if (onFocusChange) {
      onFocusChange({ setIndex, field });
    }
  };
  
  const handleKeyDown = (e) => {
    if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Backspace', 'Delete'].includes(e.key)) {
      return; 
    }
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e, setIndex, field) => {
    const value = e.target.value;
    if (/^[0-9]{0,3}$/.test(value)) {
      // GEÄNDERT: Ruft onSetChange mit setIndex statt laneKey auf.
      onSetChange(setIndex, field, value === '' ? '' : parseInt(value, 10));
    }
  };

  return (
    <table className="tablet-score-table">
      <thead>
        <tr>
          {/* GEÄNDERT: "Bahn" zu "Satz" */}
          <th>Satz</th><th>Volle</th><th>Abr.</th><th>FW</th><th>Gesamt</th>
        </tr>
      </thead>
      <tbody>
        {/* GEÄNDERT: Dynamisches Rendern basierend auf dem 'sets'-Array */}
        {sets.map((set, index) => {
          const isActive = activeInput?.setIndex === index;
          return (
            <tr key={index}>
              <td>{index + 1}</td>
              {['volle', 'abr', 'fw'].map(field => {
                const isFieldActive = isActive && activeInput?.field === field;
                const refKey = `set-${index}-${field}`;
                return (
                  <td key={field}>
                    <input
                      ref={el => inputRefs.current[refKey] = el}
                      type="number"
                      inputMode="numeric"
                      value={set?.[field] ?? ''}
                      onFocus={() => handleFocus(index, field)}
                      onChange={(e) => handleChange(e, index, field)}
                      onKeyDown={handleKeyDown}
                      className={isFieldActive ? 'active-input' : ''}
                    />
                  </td>
                );
              })}
              <td>{(Number(set?.volle) || 0) + (Number(set?.abr) || 0)}</td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <th>Gesamt</th>
          <th>{totals.totalVolle}</th>
          <th>{totals.totalAbr}</th>
          <th>{totals.totalFw}</th>
          <th>{totals.totalGesamt}</th>
        </tr>
      </tfoot>
    </table>
  );
};

export default PlayerScoreEntry;